import {
  Badge,
  Button,
  Card,
  CardContent,
  Tabs,
  TabsList,
  TabsTrigger,
  toast,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3Icon,
  DownloadIcon,
  InfoIcon,
  PrinterIcon,
  TableIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { parseFiltSearchParam } from "@/modules/forms/domain/lib/answer-filters-query";
import { FORM_RESPONSE_SIMILARITY_THRESHOLD_PERCENT } from "@/modules/forms/domain/lib/form-response-similarity";
import type { FormResponsesApi } from "@/modules/forms/presentation/api/form-responses-api";
import { useDeleteFormResponseMutation } from "@/modules/forms/presentation/api/queries";
import type { FormFieldDTO } from "@/modules/forms/presentation/api/types";
import { FormResponsesAnswerFiltersBar } from "@/modules/forms/presentation/ui/responses/FormResponsesAnswerFiltersBar";
import { FormResponsesCharts } from "@/modules/forms/presentation/ui/responses/FormResponsesCharts";
import { FormResponsesDataTable } from "@/modules/forms/presentation/ui/responses/FormResponsesDataTable";
import { FormResponsesPrintPdfDialog } from "@/modules/forms/presentation/ui/responses/FormResponsesPrintPdfDialog";
import { QueryState } from "@/shared/ui/QueryState";

export type FormResponsesSearch = {
  limit: number;
  offset: number;
  filt?: string;
};

type FormResponsesPanelProps = {
  formId: string;
  formTitle: string;
  fields: FormFieldDTO[];
  responsesApi: FormResponsesApi;
  search: FormResponsesSearch;
  onNavigateSearch: (next: {
    limit?: number;
    offset: number;
    filt?: string;
  }) => void;
  /** ໜ້າ admin ມີ header ຢູ່ນອກ panel ແລ້ວ */
  hidePageHeader?: boolean;
  headerActions?: React.ReactNode;
};

const tabTriggerClass =
  "gap-2 whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-4 py-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary sm:px-6";

export function FormResponsesPanel({
  formId,
  formTitle,
  fields,
  responsesApi,
  search,
  onNavigateSearch,
  hidePageHeader = false,
  headerActions,
}: FormResponsesPanelProps) {
  const { limit, offset, filt } = search;
  const [tab, setTab] = useState("table");
  const [printPdfOpen, setPrintPdfOpen] = useState(false);
  const canUpdate = useActionPermission("forms:update");
  const deleteResponse = useDeleteFormResponseMutation(formId);

  const activeFilterCount = useMemo(
    () => Object.keys(parseFiltSearchParam(filt)).length,
    [filt],
  );

  const responses = useQuery({
    queryKey: ["form-responses", formId, { limit, offset, filt }, responsesApi],
    queryFn: () => responsesApi.listResponses(formId, { limit, offset, filt }),
    enabled: !!formId,
  });

  const summary = useQuery({
    queryKey: ["form-responses-summary", formId, responsesApi],
    queryFn: () => responsesApi.getResponsesSummary(formId),
    enabled: !!formId && tab === "charts",
  });

  const tableLoading = responses.isPending || responses.isFetching;
  const totalCount = responses.data?.meta.total ?? 0;
  const pageFrom = totalCount === 0 ? 0 : offset + 1;
  const pageTo = Math.min(offset + limit, totalCount);

  return (
    <div className="flex flex-col gap-6">
      {!hidePageHeader ? (
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="font-semibold text-2xl tracking-tight">
              ຜົນຕອບຟອມ
            </h1>
            <p className="text-muted-foreground text-sm">{formTitle}</p>
          </div>
          {headerActions ? (
            <div className="flex flex-wrap gap-2">{headerActions}</div>
          ) : null}
        </div>
      ) : null}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <div className="overflow-x-auto border-b">
          <TabsList className="h-auto w-full min-w-max bg-transparent p-0">
            <TabsTrigger value="table" className={tabTriggerClass}>
              <TableIcon className="h-4 w-4" />
              ຕາຕະລາງ
              {totalCount > 0 ? (
                <Badge variant="secondary" className="ml-1 tabular-nums">
                  {totalCount}
                </Badge>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="charts" className={tabTriggerClass}>
              <BarChart3Icon className="h-4 w-4" />
              ສະຫຼຸບ
            </TabsTrigger>
          </TabsList>
        </div>

        {tab === "table" ? (
          <div className="mt-6 flex flex-col gap-6">
            <Card>
              <CardContent className="flex flex-wrap items-center justify-between gap-4">
                <div className="space-y-1">
                  <p className="font-medium text-sm">
                    {totalCount === 0
                      ? "ຍັງບໍ່ມີຄຳຕອບ"
                      : `${totalCount} ຄຳຕອບ`}
                    {activeFilterCount > 0 ? (
                      <span className="text-muted-foreground">
                        {" "}
                        · ກອງ {activeFilterCount} ຕົວ
                      </span>
                    ) : null}
                  </p>
                  {totalCount > 0 ? (
                    <p className="text-muted-foreground text-xs tabular-nums">
                      ສະແດງ {pageFrom}–{pageTo}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={totalCount === 0}
                    onClick={() =>
                      toast.promise(
                        responsesApi.downloadResponsesCsv(formId, { filt }),
                        {
                          loading: "ກຳລັງດາວນໂຫຼດ CSV...",
                          success: "ດາວນໂຫຼດແລ້ວ",
                          error: "ດາວນໂຫຼດບໍ່ສຳເລັດ",
                        },
                      )
                    }
                  >
                    <DownloadIcon className="mr-2 h-4 w-4" />
                    CSV
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={totalCount === 0}
                    onClick={() => setPrintPdfOpen(true)}
                  >
                    <PrinterIcon className="mr-2 h-4 w-4" />
                    PDF (A4)
                  </Button>
                </div>
              </CardContent>
            </Card>

            <FormResponsesPrintPdfDialog
              open={printPdfOpen}
              onOpenChange={setPrintPdfOpen}
              formId={formId}
              fields={fields}
              filt={filt}
              responsesApi={responsesApi}
            />

            <FormResponsesAnswerFiltersBar
              fields={fields}
              filtJson={filt}
              onNavigate={({ filt: nextFilt, offset: nextOffset }) =>
                onNavigateSearch({
                  offset: nextOffset,
                  filt: nextFilt,
                  limit,
                })
              }
            />

            <details className="group rounded-xl border bg-muted/30 px-6 py-4 text-muted-foreground text-xs">
              <summary className="flex cursor-pointer list-none items-center gap-2 font-medium text-foreground marker:content-none">
                <InfoIcon className="h-3.5 w-3.5 shrink-0" />
                ຄວາມຄ້າຍຄືຄຳຕອບ
                <span className="text-muted-foreground group-open:hidden">
                  (≥ {FORM_RESPONSE_SIMILARITY_THRESHOLD_PERCENT}%)
                </span>
              </summary>
              <p className="mt-3 leading-relaxed">
                ຄວາມຄ້າຍຄື ≥ {FORM_RESPONSE_SIMILARITY_THRESHOLD_PERCENT}% —
                ປຸ່ມ「ລຶບຊ້ຳ」ສຳລັບຕົວຊ້ຳ, ປຸ່ມ「ລຶບ」ຈາງກວ່າສຳລັບຄຳຕອບຄ້າຍກັນ
                {canUpdate
                  ? " (ຕ້ອງມີສິດແກ້ໄຂຟອມ)"
                  : ""}
              </p>
            </details>

            <FormResponsesDataTable
              formId={formId}
              fields={fields}
              data={responses.data?.data ?? []}
              offset={offset}
              limit={limit}
              totalCount={totalCount}
              isLoading={tableLoading}
              responsesApi={responsesApi}
              canDeleteSimilar={canUpdate}
              deletePending={deleteResponse.isPending}
              onDeleteSimilar={async (responseId) => {
                try {
                  await deleteResponse.mutateAsync(responseId);
                } catch {
                  /* toast ໃນ mutation */
                }
              }}
              onPaginationChange={(nextOffset, nextLimit) =>
                onNavigateSearch({
                  offset: nextOffset,
                  limit: nextLimit,
                  filt,
                })
              }
            />
          </div>
        ) : (
          <div className="mt-6">
            <QueryState result={summary} title="ກໍາລັງໂຫຼດສະຫຼຸບ">
              <FormResponsesCharts summary={summary.data ?? null} />
            </QueryState>
          </div>
        )}
      </Tabs>
    </div>
  );
}
